import { imageSizes, imageSrcSet, imageUrl } from "@components/componentUtils"
import { AstroNewsViewTransitions } from "@components/AstroViewTransitions"
import NewsCard from "@components/News/NewsCard"
import "@components/News/NewsDetail/styles.css"
import { BackButton } from "@components/SiteShell"
import { SiteShell } from "@components/SiteShell"
import { utcDateOnly } from "@dashboard-config"
import { newsDetailPageQueryOptions } from "@data/publicPageDataQuery"
import { sanitizeRichHtml } from "@lib/sanitizeHtml"
import type { NewsItem } from "@site-types/content"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { isSupportedLocale } from "@utils/locale"
import { localizedRouteHead } from "@utils/routeHead"

export const Route = createFileRoute("/$locale/news/$slug")({
  beforeLoad: ({ params }) => {
    if (!isSupportedLocale(params.locale)) throw redirect({ to: "/404" })
  },
  loader: async ({ context, params }) => {
    const locale = params.locale as "en" | "ja"
    const data = await context.queryClient.ensureQueryData(newsDetailPageQueryOptions(locale, params.slug))
    if (!data) throw redirect({ href: `/${locale}/news` })
    return data
  },
  head: ({ params, loaderData }) => {
    const locale = params.locale as "en" | "ja"
    const post = loaderData?.post
    const head = localizedRouteHead({
      locale,
      page: "news",
      path: `/${locale}/news/${params.slug}`,
      title: post?.title ? `${post.title} | PLAYBRAIN Inc.` : undefined,
      description: post?.metaOgDescription || post?.metaDescription || post?.newsCoverBlurb,
      image: post?.newsCoverImage ? (imageUrl(post.newsCoverImage, "newsDetail") ?? undefined) : undefined,
      type: "article",
    })
    if (post?.newsCoverImage) {
      head.links.push({
        rel: "preload",
        as: "image",
        href: imageUrl(post.newsCoverImage, "newsDetail") ?? "",
        imageSrcSet: imageSrcSet(post.newsCoverImage, "newsDetail"),
        imageSizes: imageSizes("newsDetail"),
        fetchPriority: "high",
      })
    }
    return head
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { post } = Route.useLoaderData()
  return (
    <SiteShell>
      <NewsDetailPage post={post} />
    </SiteShell>
  )
}

export function NewsDetailPage({ post }: { post: NewsItem }) {
  const displayDate = post.publishDate ? (utcDateOnly(post.publishDate) ?? "") : ""
  return (
    <div className="news-detail-container container">
      <AstroNewsViewTransitions news={[post]} />
      <div className="back-button">
        <BackButton />
      </div>
      <article className="post">
        <h1 className="title">{post.title}</h1>
        <p className="date">
          <i className="fa-sharp fa-regular fa-newspaper" />
          {displayDate}
        </p>
        <NewsCard
          post={post}
          detail
        />
        <blockquote className="blurb">{post.newsCoverBlurb}</blockquote>
        <div
          className="news-detail-body"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(post.bio) }}
        />
      </article>
    </div>
  )
}
